import React, { useRef, useEffect, useState } from 'react';
import { AIMessage } from './AIMessage';
import { ThinkingIndicator } from './ThinkingIndicator';
import { WelcomeScreen } from './WelcomeScreen';
import type { AIMessageListProps, AIQuestionAnswers } from './types';

interface ExtendedMessageListProps extends AIMessageListProps {
  onSuggestionClick?: (text: string) => void;
  businessLogoUrl?: string | null;
  businessId?: string | null;
  onQuestionsSubmit?: (answers: AIQuestionAnswers, displayText: string) => void;
}

export const AIMessageList: React.FC<ExtendedMessageListProps> = ({
  messages,
  isLoading,
  onRevert,
  onConfirmTools,
  onSuggestionClick,
  businessLogoUrl,
  businessId,
  onQuestionsSubmit,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Track message IDs that existed on first render - these should NEVER animate
  const initialMessageIdsRef = useRef<Set<string> | null>(null);

  // Track which new messages have already been animated
  const animatedNewMessagesRef = useRef<Set<string>>(new Set());

  // The single message ID currently being animated (if any)
  const [animatingId, setAnimatingId] = useState<string | null>(null);

  // On first render with messages, capture all existing IDs
  // These are "old" messages loaded from DB and should never animate
  if (initialMessageIdsRef.current === null && messages.length > 0) {
    initialMessageIdsRef.current = new Set(messages.map(m => m.id));
  }

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Detect truly new assistant messages and trigger animation
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    // Only animate if:
    // 1. It's an assistant message
    // 2. It wasn't in the initial load (not in initialMessageIdsRef)
    // 3. It hasn't been animated yet (not in animatedNewMessagesRef)
    const isAssistant = lastMessage.role === 'assistant';
    const wasInitial = initialMessageIdsRef.current?.has(lastMessage.id) ?? false;
    const alreadyAnimated = animatedNewMessagesRef.current.has(lastMessage.id);

    if (isAssistant && !wasInitial && !alreadyAnimated) {
      // This is a brand new message - animate it
      setAnimatingId(lastMessage.id);
      animatedNewMessagesRef.current.add(lastMessage.id);
    }
  }, [messages]);

  if (messages.length === 0 && !isLoading) {
    return (
      <WelcomeScreen
        onSuggestionClick={onSuggestionClick || (() => {})}
      />
    );
  }

  return (
    <div className="flex flex-col">
      {messages.map((message) => {
        // Only pass isLatest=true for the message currently being animated
        const shouldAnimate = message.id === animatingId;

        return (
          <AIMessage
            key={message.id}
            message={message}
            onRevert={onRevert}
            onConfirmTools={onConfirmTools}
            isLatest={shouldAnimate}
            businessLogoUrl={businessLogoUrl}
            businessId={businessId}
            onQuestionsSubmit={onQuestionsSubmit}
          />
        );
      })}

      {/* Thinking indicator */}
      {isLoading && <ThinkingIndicator />}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default AIMessageList;
